import { useCallback } from 'react';
import { STRIPE_PRODUCT_IDS } from '@cx-web/repub-config';

import { type MetaPixelEventName, type MetaPixelSubscriptionClickParams } from '../types/meta-pixel-events';

interface UseTrackGZMetaPixelSubscriptionClickConfig {
  ctaName?: string;
  externalUrl?: string;
  productId: string;
}

interface UrlParams {
  offerId: string | null;
  plan: string | null;
}

function extractParamsFromUrl(url: string): UrlParams {
  try {
    const urlObj = new URL(url);
    return {
      offerId: urlObj.searchParams.get('OfferId'),
      plan: urlObj.searchParams.get('pwcmp'),
    };
  } catch {
    return { offerId: null, plan: null };
  }
}

function getBillingModel(productId: string): 'monthly' | 'quarterly' | undefined {
  if (productId === STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_MONTHLY) {
    return 'monthly';
  }
  if (productId === STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_THREE_MONTHLY) {
    return 'quarterly';
  }
  return undefined;
}

/**
 * React hook that creates a tracking function for GZ Meta Pixel subscription click events.
 */
export function useTrackGZMetaPixelSubscriptionClick(config: UseTrackGZMetaPixelSubscriptionClickConfig): () => void {
  const { ctaName, externalUrl, productId } = config;

  const trackClick = useCallback(() => {
    // Check if Meta Pixel is loaded
    if (!window.fbq) {
      return;
    }

    // Check consent
    if (!window.iframeGZContext?.consentGranted) {
      return;
    }

    // Check if external URL exists
    if (!externalUrl) {
      return;
    }

    // Extract required parameters from URL (single parse)
    const { offerId, plan } = extractParamsFromUrl(externalUrl);

    // Don't fire event if required parameters are missing
    if (!offerId || !plan) {
      return;
    }

    // Build event parameters
    const params: MetaPixelSubscriptionClickParams = {
      offer_id: offerId,
      plan,
      funnel_name: 'AI Predictor',
      landing_name: 'AI Predictor',
      landing_url: window.location.href,
      product_type: 'paid_subscription',
    };

    // Add optional parameters
    if (ctaName) {
      params.cta_name = ctaName;
    }

    const billingModel = getBillingModel(productId);
    if (billingModel) {
      params.billing_model = billingModel;
    }

    // Fire Meta Pixel custom event
    const eventName: MetaPixelEventName = 'ClickSubscribeCTA';
    window.fbq('trackCustom', eventName, params);
  }, [productId, externalUrl, ctaName]);

  return trackClick;
}
